import { useCallback, useEffect, useRef, useState } from 'react';
import { fireViewEvent, fireTapEvent, fireGenerateLead } from '../utils/tracking';
import { buildPayload } from '../utils/formUtils';
import { ACCENT, ACCENT_DARK, TEAL } from '../utils/theme';

/**
 * GAS WebアプリURL
 * 2026-07-16 リードDB統一: 旧URL(AKfycbz1…)は403でCV全件ロスト状態だった。
 * gas-questionnaire経由でAIシート「LP完結リード」へ集約（lp02と同じ経路）
 */
const GAS_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 0 | 1 | 2 | 3 | 4 | 5;

/** DBカラムと一致させる選択肢 */
const CREDENTIALS = ['正看護師', '准看護師', '保健師', '助産師'] as const;
const TIMINGS     = [
  '今すぐに',
  '1ヶ月以内',
  '3ヶ月以内',
  '6ヶ月以内',
  '1年以内',
  '良い求人があれば',
] as const;

// ── ステップ計測設定 ──────────────────────────────────
const STEP_TRACK = [
  { elementId: 'modal_intro',   elementName: 'フォームモーダル導入', elementIndex: 0 },
  { elementId: 'form_step_1',   elementName: '保有資格',            elementIndex: 1 },
  { elementId: 'form_step_2',   elementName: '転職希望時期',         elementIndex: 2 },
  { elementId: 'form_step_3',   elementName: '名前と郵便番号',       elementIndex: 3 },
  { elementId: 'form_step_4',   elementName: '電話番号',            elementIndex: 4 },
  { elementId: 'form_complete', elementName: '完了画面',            elementIndex: 5 },
];

// ── GAS 送信 ─────────────────────────────────────────
/**
 * フォームデータを JSON で GAS に送信する。
 * no-cors モードのため Content-Type は text/plain、ボディは JSON 文字列。
 * GAS 側は e.postData.contents を JSON.parse() して受け取る。
 */
async function sendToGas(formData: {
  qualification: string;
  transfer_timing: string;
  name: string;
  postal_code: string;
  phone: string;
}): Promise<void> {
  const payload = buildPayload(formData);
  const body = JSON.stringify(payload);

  // デバッグ用ログ（開発・確認時のみ）
  console.log('[ニジ看護転職] 送信ペイロード:', payload);

  try {
    await fetch(GAS_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      // no-cors で許可された CORS-safelisted content-type を使用
      headers: { 'Content-Type': 'text/plain' },
      body,
    });
  } catch (err) {
    // no-cors の場合、ネットワークエラー以外は catch されない
    console.error('[GAS送信エラー]', err);
    throw err; // 呼び出し元で再試行可能にするため再スロー
  }
}

// ── メインコンポーネント ──────────────────────────────
export default function FormModal({ isOpen, onClose }: FormModalProps) {
  const [step, setStep]               = useState<Step>(0);
  const [direction, setDirection]     = useState<'forward' | 'back'>('forward');
  const [transitioning, setTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [submitError, setSubmitError]     = useState(false);

  // フォーム値
  const [selectedCredentials, setSelectedCredentials] = useState<string[]>([]);
  const [selectedTiming, setSelectedTiming]           = useState('');
  const [name, setName]             = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // バリデーションエラー
  const [nameError, setNameError]     = useState('');
  const [postalError, setPostalError] = useState('');
  const [phoneError, setPhoneError]   = useState('');

  const contentRef = useRef<HTMLDivElement>(null);

  // ステップ変化時に GA4 ビューイベント発火
  useEffect(() => {
    if (!isOpen) return;
    const cfg = STEP_TRACK[step];
    if (!cfg) return;
    fireViewEvent({
      elementId: cfg.elementId,
      elementType: step === 0 || step === 5 ? 'modal_screen' : 'form_step',
      elementName: cfg.elementName,
      elementIndex: cfg.elementIndex,
    });
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [step, isOpen]);

  // モーダル開閉時の body スクロール制御
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // クローズ時にフォーム状態リセット
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setStep(0);
        setSelectedCredentials([]);
        setSelectedTiming('');
        setName('');
        setPostalCode('');
        setPhoneNumber('');
        setNameError('');
        setPostalError('');
        setPhoneError('');
        setDirection('forward');
        setIsSubmitting(false);
        setSubmitError(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const goTo = useCallback(
    (next: Step, dir: 'forward' | 'back') => {
      if (transitioning) return;
      setDirection(dir);
      setTransitioning(true);
      setTimeout(() => {
        setStep(next);
        setTransitioning(false);
      }, 220);
    },
    [transitioning]
  );

  const handleNext = async () => {
    if (step === 0) { goTo(1, 'forward'); return; }

    if (step === 1) {
      fireTapEvent({ elementId: 'form_next_1', elementType: 'button', elementName: '次へ_STEP1', elementIndex: 1 });
      goTo(2, 'forward');
      return;
    }

    if (step === 2) {
      fireTapEvent({ elementId: 'form_next_2', elementType: 'button', elementName: '次へ_STEP2', elementIndex: 2 });
      goTo(3, 'forward');
      return;
    }

    if (step === 3) {
      let hasError = false;
      if (!name.trim()) { setNameError('お名前を入力してください'); hasError = true; }
      if (postalCode.length !== 7) { setPostalError('7桁で入力してください'); hasError = true; }
      if (hasError) return;
      setNameError('');
      setPostalError('');
      fireTapEvent({ elementId: 'form_next_3', elementType: 'button', elementName: '次へ_STEP3', elementIndex: 3 });
      goTo(4, 'forward');
      return;
    }

    if (step === 4) {
      const normalizedPhone = phoneNumber.replace(/-/g, '');
      if (normalizedPhone.length !== 11) {
        setPhoneError('電話番号を確認してください（11桁）');
        return;
      }
      setPhoneError('');
      setSubmitError(false);
      fireTapEvent({ elementId: 'form_submit_4', elementType: 'button', elementName: '確認して送信する', elementIndex: 4 });

      setIsSubmitting(true);
      try {
        await sendToGas({
          qualification:   selectedCredentials.join(', '),
          transfer_timing: selectedTiming,
          name:            name.trim(),
          postal_code:     postalCode.replace(/-/g, ''),
          phone:           normalizedPhone,
        });
        // ✅ コンバージョンイベント送信（送信成功確定後に1回だけ発火）
        fireGenerateLead({
          qualification:   selectedCredentials.join(', '),
          transfer_timing: selectedTiming,
        });
        goTo(5, 'forward');
      } catch {
        // 送信失敗 – ユーザーに再試行を促す
        setSubmitError(true);
        // no-cors では常に成功扱いになるため、実際には catch されることは稀
        goTo(5, 'forward');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step <= 1) return;
    goTo((step - 1) as Step, 'back');
  };

  const handleNumericInput = (value: string, setter: (v: string) => void) => {
    setter(value.replace(/[^0-9]/g, ''));
  };

  if (!isOpen) return null;

  const progressPercent = step >= 1 && step <= 4 ? (step / 4) * 100 : 0;
  const animClass = transitioning
    ? 'opacity-0'
    : direction === 'forward'
    ? 'step-enter'
    : 'step-enter-back';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        className="modal-enter"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 430,
          background: '#fff',
          borderRadius: 20,
          maxHeight: '88dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            padding: '16px 20px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 48 }}>
            {step >= 2 && step <= 4 && (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#aaa',
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: '4px 0',
                  whiteSpace: 'nowrap',
                }}
              >
                ← 戻る
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500 }}>
            {step >= 1 && step <= 4 && `${step} / 4`}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#f5f5f5',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className="ri-close-line" style={{ fontSize: 16, color: '#666' }} />
          </button>
        </div>

        {/* プログレスバー */}
        {step >= 1 && step <= 4 && (
          <div
            style={{
              height: 3,
              background: '#f0f0f0',
              margin: '12px 20px 0',
              borderRadius: 2,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPercent}%`,
                background: `linear-gradient(90deg, ${ACCENT}, #e8829a)`,
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        )}

        {/* スクロール可能コンテンツ */}
        <div
          ref={contentRef}
          style={{ overflowY: 'auto', flex: 1, padding: '20px 20px 32px' }}
        >
          <div className={animClass}>
            {step === 0 && <StepIntro onNext={handleNext} />}
            {step === 1 && (
              <StepCredentials
                selected={selectedCredentials}
                onChange={setSelectedCredentials}
                onNext={handleNext}
              />
            )}
            {step === 2 && (
              <StepTiming
                selected={selectedTiming}
                onChange={setSelectedTiming}
                onNext={handleNext}
              />
            )}
            {step === 3 && (
              <StepNameAndPostal
                name={name}
                nameError={nameError}
                onNameChange={(v) => { setName(v); setNameError(''); }}
                postalCode={postalCode}
                postalError={postalError}
                onPostalChange={(v) => { handleNumericInput(v, setPostalCode); setPostalError(''); }}
                onNext={handleNext}
              />
            )}
            {step === 4 && (
              <StepPhone
                value={phoneNumber}
                error={phoneError}
                submitError={submitError}
                onChange={(v) => { handleNumericInput(v, setPhoneNumber); setPhoneError(''); }}
                onNext={handleNext}
                isSubmitting={isSubmitting}
              />
            )}
            {step === 5 && <StepComplete onClose={onClose} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STEP 0: イントロ ─────────────────────────────────
function StepIntro({ onNext }: { onNext: () => void }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 8 }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: `${ACCENT}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <i className="ri-heart-pulse-line" style={{ fontSize: 26, color: ACCENT }} />
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#2a2a2a', lineHeight: 1.6, marginBottom: 12 }}>
        条件に合う職場をお探しします
      </h2>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
        所要時間：<strong style={{ color: TEAL }}>1〜2分</strong>
      </p>
      <p style={{ fontSize: 12, color: '#bbb', marginBottom: 32 }}>4つの質問にお答えください</p>
      <button
        onClick={onNext}
        style={{
          width: '100%',
          background: `linear-gradient(135deg, ${ACCENT}, #e8829a)`,
          color: '#fff',
          border: 'none',
          borderRadius: 12,
          padding: '16px',
          fontSize: 15,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(245,160,177,0.35)',
          whiteSpace: 'nowrap',
        }}
      >
        次へ進む
      </button>
    </div>
  );
}

// ── STEP 1: 保有資格 ─────────────────────────────────
function StepCredentials({
  selected,
  onChange,
  onNext,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
  onNext: () => void;
}) {
  const toggle = (item: string) =>
    onChange(
      selected.includes(item)
        ? selected.filter((s) => s !== item)
        : [...selected, item]
    );

  return (
    <div>
      <StepTitle step={1} title="保有している資格を教えてください" sub="複数選択できます" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {CREDENTIALS.map((c) => {
          const active = selected.includes(c);
          return (
            <button
              key={c}
              onClick={() => toggle(c)}
              className="card-select-anim"
              style={{
                border: `2px solid ${active ? ACCENT : '#eee'}`,
                background: active ? `${ACCENT}18` : '#fff',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                color: active ? '#c0536a' : '#444',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                whiteSpace: 'nowrap',
              }}
            >
              {c}
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${active ? ACCENT : '#ddd'}`,
                  background: active ? ACCENT : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
              >
                {active && <i className="ri-check-line" style={{ fontSize: 11, color: '#fff' }} />}
              </div>
            </button>
          );
        })}
      </div>
      <NextButton onClick={onNext} disabled={selected.length === 0} />
    </div>
  );
}

// ── STEP 2: 転職希望時期 ─────────────────────────────
function StepTiming({
  selected,
  onChange,
  onNext,
}: {
  selected: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  return (
    <div>
      <StepTitle step={2} title={"職場を変えるとしたら\nいつ頃をお考えですか"} sub="焦る必要はありません" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {TIMINGS.map((t) => {
          const active = selected === t;
          return (
            <button
              key={t}
              onClick={() => onChange(t)}
              className="card-select-anim"
              style={{
                border: `2px solid ${active ? TEAL : '#eee'}`,
                background: active ? `${TEAL}18` : '#fff',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                color: active ? '#2a8a7e' : '#444',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                whiteSpace: 'nowrap',
              }}
            >
              {t}
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${active ? TEAL : '#ddd'}`,
                  background: active ? TEAL : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
              >
                {active && <i className="ri-check-line" style={{ fontSize: 11, color: '#fff' }} />}
              </div>
            </button>
          );
        })}
      </div>
      <NextButton onClick={onNext} disabled={!selected} />
    </div>
  );
}

// ── STEP 3: 名前 + 郵便番号 ─────────────────────────
function StepNameAndPostal({
  name,
  nameError,
  onNameChange,
  postalCode,
  postalError,
  onPostalChange,
  onNext,
}: {
  name: string;
  nameError: string;
  onNameChange: (v: string) => void;
  postalCode: string;
  postalError: string;
  onPostalChange: (v: string) => void;
  onNext: () => void;
}) {
  return (
    <div>
      <StepTitle step={3} title={"お名前と郵便番号を\n教えてください"} />

      {/* 名前 */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{ fontSize: 12, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}
        >
          お名前
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="例：山田 花子"
          style={{
            width: '100%',
            border: `2px solid ${nameError ? '#e85c6a' : name.trim() ? TEAL : '#eee'}`,
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 16,
            fontWeight: 600,
            color: '#2a2a2a',
            outline: 'none',
            background: '#fafafa',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
        />
        {nameError && (
          <p style={{ fontSize: 12, color: '#e85c6a', marginTop: 6 }}>{nameError}</p>
        )}
      </div>

      {/* 郵便番号 */}
      <div style={{ marginBottom: 8 }}>
        <label
          style={{ fontSize: 12, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}
        >
          郵便番号（ハイフンなし7桁）
        </label>
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={7}
          value={postalCode}
          onFocus={() =>
            fireTapEvent({
              elementId: 'input_postal_code',
              elementType: 'input',
              elementName: '郵便番号',
              elementIndex: 3,
              tapTarget: 'input',
            })
          }
          onChange={(e) => onPostalChange(e.target.value)}
          data-track-tap="true"
          data-element-id="input_postal_code"
          data-element-type="input"
          data-element-name="郵便番号"
          data-element-index="3"
          placeholder="例：1000001"
          style={{
            width: '100%',
            border: `2px solid ${postalError ? '#e85c6a' : postalCode.length === 7 ? TEAL : '#eee'}`,
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '0.15em',
            color: '#2a2a2a',
            outline: 'none',
            background: '#fafafa',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
        />
        {postalError && (
          <p style={{ fontSize: 12, color: '#e85c6a', marginTop: 6 }}>{postalError}</p>
        )}
      </div>

      <div style={{ marginBottom: 28 }}>
        <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.8 }}>
          <i className="ri-map-pin-line" style={{ marginRight: 4, color: TEAL }} />
          エリアに合った職場をご紹介するために使用します
        </p>
      </div>

      <NextButton onClick={onNext} disabled={false} />
    </div>
  );
}

// ── STEP 4: 電話番号 ─────────────────────────────────
function StepPhone({
  value,
  error,
  submitError,
  onChange,
  onNext,
  isSubmitting,
}: {
  value: string;
  error: string;
  submitError: boolean;
  onChange: (v: string) => void;
  onNext: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div>
      <StepTitle step={4} title={"ご連絡先の電話番号を\n教えてください"} sub="担当者からご連絡いたします" />
      <div style={{ marginBottom: 8 }}>
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={11}
          value={value}
          onFocus={() =>
            fireTapEvent({
              elementId: 'input_phone',
              elementType: 'input',
              elementName: '電話番号',
              elementIndex: 4,
              tapTarget: 'input',
            })
          }
          onChange={(e) => onChange(e.target.value)}
          data-track-tap="true"
          data-element-id="input_phone"
          data-element-type="input"
          data-element-name="電話番号"
          data-element-index="4"
          placeholder="例：09012345678"
          style={{
            width: '100%',
            border: `2px solid ${
              error
                ? '#e85c6a'
                : value.length >= 10 && value.length <= 11
                ? TEAL
                : '#eee'
            }`,
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#2a2a2a',
            outline: 'none',
            background: '#fafafa',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
        />
        {error && <p style={{ fontSize: 12, color: '#e85c6a', marginTop: 6 }}>{error}</p>}
        {submitError && (
          <p style={{ fontSize: 12, color: '#e85c6a', marginTop: 6 }}>
            送信に失敗しました。もう一度お試しください。
          </p>
        )}
      </div>
      <div style={{ marginBottom: 28 }}>
        <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.8 }}>
          <i className="ri-heart-line" style={{ marginRight: 4, color: ACCENT }} />
          無理な転職のご提案はいたしません
        </p>
        <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.8 }}>
          <i className="ri-chat-smile-line" style={{ marginRight: 4, color: ACCENT }} />
          ご希望が固まっていなくても問題ありません
        </p>
      </div>
      <NextButton
        onClick={onNext}
        disabled={isSubmitting}
        label={isSubmitting ? '送信中...' : '確認して送信する'}
      />
    </div>
  );
}

// ── STEP 5: 完了 ─────────────────────────────────────
function StepComplete({ onClose }: { onClose: () => void }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 12 }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: `${TEAL}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <i className="ri-checkbox-circle-line" style={{ fontSize: 30, color: TEAL }} />
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#2a2a2a', marginBottom: 16, lineHeight: 1.6 }}>
        ご入力ありがとうございます
      </h2>
      <div
        style={{
          background: '#F8FFFE',
          border: `1px solid ${TEAL}44`,
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 32,
          textAlign: 'left',
        }}
      >
        <p style={{ fontSize: 13, color: '#444', lineHeight: 1.85 }}>
          専任の担当者が、あなたの希望条件をもとにご提案します。<br />
          まずは簡単なヒアリングからスタートします
          <span style={{ fontWeight: 700, color: TEAL }}>（5〜10分程度）</span>。<br />
          <strong>お電話またはLINE</strong>でご連絡いたします。
        </p>
      </div>
      <button
        onClick={onClose}
        style={{
          width: '100%',
          background: '#f5f5f5',
          color: '#666',
          border: 'none',
          borderRadius: 12,
          padding: '14px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        閉じる
      </button>
    </div>
  );
}

// ── 共通パーツ ────────────────────────────────────────
function StepTitle({
  step,
  title,
  sub,
}: {
  step: number;
  title: string;
  sub?: string;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p
        style={{
          fontSize: 11,
          color: ACCENT,
          fontWeight: 700,
          marginBottom: 8,
          letterSpacing: '0.05em',
        }}
      >
        STEP {step}
      </p>
      <h3
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: '#2a2a2a',
          lineHeight: 1.65,
          marginBottom: sub ? 6 : 0,
          whiteSpace: 'pre-line',
        }}
      >
        {title}
      </h3>
      {sub && <p style={{ fontSize: 12, color: '#aaa' }}>{sub}</p>}
    </div>
  );
}

function NextButton({
  onClick,
  disabled,
  label = '次へ',
}: {
  onClick: () => void;
  disabled: boolean;
  label?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        background: disabled ? '#eee' : `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
        color: disabled ? '#bbb' : '#fff',
        border: 'none',
        borderRadius: 12,
        padding: '16px',
        fontSize: 15,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : `0 4px 16px ${ACCENT}50`,
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
